import React, { useCallback, useEffect, useId, useRef, useState } from 'react'

const HOLD_MS = 1000
const END_THRESHOLD = 0.94
const INTENT_DISTANCE = 12
const HANDLE_INSET = 8

/** A deliberate pointer drag or keyboard hold; clicking never confirms. */
export default function SlideToConfirm({ label, onConfirm, disabled = false, resetKey }) {
  const instructionsId = useId()
  const trackRef = useRef(null)
  const handleRef = useRef(null)
  const gestureRef = useRef(null)
  const frameRef = useRef(null)
  const [progress, setProgress] = useState(0)

  const reset = useCallback(() => {
    const gesture = gestureRef.current
    gestureRef.current = null
    if (frameRef.current !== null) cancelAnimationFrame(frameRef.current)
    frameRef.current = null
    if (gesture?.pointerId !== undefined && trackRef.current?.hasPointerCapture(gesture.pointerId)) {
      trackRef.current.releasePointerCapture(gesture.pointerId)
    }
    setProgress(0)
  }, [])

  useEffect(() => { reset() }, [disabled, resetKey, label, reset])
  useEffect(() => {
    const hide = () => { if (document.visibilityState !== 'visible') reset() }
    document.addEventListener('visibilitychange', hide)
    window.addEventListener('blur', reset)
    return () => {
      document.removeEventListener('visibilitychange', hide)
      window.removeEventListener('blur', reset)
      if (frameRef.current !== null) cancelAnimationFrame(frameRef.current)
      gestureRef.current = null
    }
  }, [reset])

  const complete = () => {
    reset()
    if (!disabled && document.visibilityState === 'visible') onConfirm()
  }

  const pointerDown = (event) => {
    if (disabled || gestureRef.current || event.button !== 0 || event.isPrimary === false) return
    const handle = handleRef.current.getBoundingClientRect()
    const track = event.currentTarget.getBoundingClientRect()
    // Starting elsewhere on the track cannot jump the handle to the end.
    if (event.clientX < track.left || event.clientX > handle.right + HANDLE_INSET
      || event.clientY < track.top || event.clientY > track.bottom) return
    gestureRef.current = { pointerId: event.pointerId, startX: event.clientX, startY: event.clientY,
      travel: Math.max(1, event.currentTarget.clientWidth - handle.width - 2 * HANDLE_INSET), horizontal: false, progress: 0 }
    event.preventDefault()
    event.currentTarget.focus({ preventScroll: true })
    event.currentTarget.setPointerCapture(event.pointerId)
  }

  const pointerMove = (event) => {
    const gesture = gestureRef.current
    if (gesture?.pointerId !== event.pointerId) return
    const dx = event.clientX - gesture.startX
    const dy = Math.abs(event.clientY - gesture.startY)
    if (!gesture.horizontal) {
      // Reject a vertical gesture before it becomes a drag. Once the user
      // starts sliding right, pointer capture follows their finger off-track.
      if (dy >= INTENT_DISTANCE && dy > Math.abs(dx)) { reset(); return }
      if (dx < INTENT_DISTANCE || dx < dy) return
      gesture.horizontal = true
    }
    gesture.progress = Math.min(1, Math.max(0, dx / gesture.travel))
    setProgress(gesture.progress)
  }

  const pointerUp = (event) => {
    const gesture = gestureRef.current
    if (gesture?.pointerId !== event.pointerId) return
    const atEnd = gesture.horizontal && (event.clientX - gesture.startX) / gesture.travel >= END_THRESHOLD && gesture.progress >= END_THRESHOLD
    if (atEnd) complete()
    else reset()
  }

  const keyDown = (event) => {
    if (['Enter', ' ', 'ArrowRight', 'ArrowLeft', 'Home', 'End', 'Escape'].includes(event.key)) event.preventDefault()
    if (event.key === 'Escape' || event.key === 'ArrowLeft' || event.key === 'Home') { reset(); return }
    if (event.key !== 'ArrowRight' || disabled || event.repeat || gestureRef.current) return
    const gesture = { keyboard: true, startedAt: performance.now() }
    gestureRef.current = gesture
    const advance = () => {
      if (gestureRef.current !== gesture) return
      const value = Math.min(1, (performance.now() - gesture.startedAt) / HOLD_MS)
      setProgress(value)
      if (value >= 1) complete()
      else frameRef.current = requestAnimationFrame(advance)
    }
    frameRef.current = requestAnimationFrame(advance)
  }

  return <div className="w-full space-y-3">
    <div ref={trackRef} role="slider" tabIndex={disabled ? -1 : 0} aria-label={label}
      aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.round(progress * 100)}
      aria-valuetext={gestureRef.current?.keyboard ? 'Keep holding Right Arrow' : progress >= END_THRESHOLD ? 'Release to confirm' : `${Math.round(progress * 100)} percent`}
      aria-orientation="horizontal" aria-disabled={disabled} aria-describedby={instructionsId}
      onPointerDown={pointerDown} onPointerMove={pointerMove} onPointerUp={pointerUp}
      onPointerCancel={reset} onLostPointerCapture={reset} onBlur={reset}
      onKeyDown={keyDown} onKeyUp={(event) => { if (event.key === 'ArrowRight') reset() }}
      className={`relative h-24 w-full select-none overflow-hidden rounded-2xl border border-primary/25 bg-primary/10 outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ${disabled ? 'cursor-not-allowed opacity-40' : 'cursor-grab active:cursor-grabbing'}`}
      style={{ touchAction: 'pan-y' }}>
      <div aria-hidden="true" className="pointer-events-none absolute inset-y-0 left-0 bg-primary/15" style={{ width: `${progress * 100}%` }} />
      <span aria-hidden="true" className="pointer-events-none absolute inset-0 flex items-center justify-center pl-24 pr-4 text-center text-base font-semibold leading-snug text-foreground">{label}</span>
      <span aria-hidden="true" className="absolute inset-y-0 w-24 p-2" style={{ left: `calc((100% - 96px) * ${progress})`, touchAction: 'none' }}>
        <span ref={handleRef} data-testid="slide-confirm-handle"
          className="pointer-events-none flex h-20 w-20 items-center justify-center rounded-xl bg-primary text-primary-foreground shadow-sm">
          <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="m9 5 7 7-7 7" /></svg>
        </span>
      </span>
    </div>
    <p id={instructionsId} className="text-xs leading-relaxed text-muted-foreground">Drag the arrow all the way right and release. With a keyboard, hold the Right Arrow key for 1 second.</p>
  </div>
}
